import { useState, useEffect } from 'react'
import { useAuth } from '../contexts/AuthContext'

interface Permissao {
  permissao_id: string
  chave: string
  nome: string
  descricao: string
  recurso: string
  acao: string
  categoria: string
  origem: string
}

export const usePermissions = () => {
  const { user } = useAuth()
  const [permissions, setPermissions] = useState<string[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const fetchPermissions = async () => {
      if (!user) {
        setPermissions([])
        setLoading(false)
        return
      }

      try {
        setLoading(true)
        setError(null)

        // Buscar ID do usuário na tabela usuarios_gestao
        const userResponse = await fetch(`/api/gestao/usuarios?email=${encodeURIComponent(user.email || '')}`)
        
        if (!userResponse.ok) {
          throw new Error('Erro ao buscar dados do usuário')
        }

        const userData = await userResponse.json()
        
        if (!userData || !userData.id) {
          throw new Error('Usuário não encontrado')
        }

        // Buscar permissões do usuário
        const permissionsResponse = await fetch(`/api/gestao/usuario-permissoes?usuario_id=${userData.id}`)
        
        if (!permissionsResponse.ok) {
          throw new Error('Erro ao buscar permissões')
        }

        const data = await permissionsResponse.json()
        const permissoesList = (data.permissoes || []) as Permissao[]
        
        // Extrair apenas as chaves das permissões
        const permissoesChaves = permissoesList.map((p: Permissao) => p.chave)
        setPermissions(permissoesChaves)
        
      } catch (err) {
        console.error('Erro ao buscar permissões:', err)
        setError(err instanceof Error ? err.message : 'Erro desconhecido')
        setPermissions([])
      } finally {
        setLoading(false)
      }
    }

    fetchPermissions()
  }, [user])

  const hasPermission = (permissao: string): boolean => {
    return permissions.includes(permissao)
  }

  const hasAnyPermission = (permissoes: string[]): boolean => {
    return permissoes.some(p => permissions.includes(p))
  }

  const hasAllPermissions = (permissoes: string[]): boolean => {
    return permissoes.every(p => permissions.includes(p))
  }

  return {
    permissions,
    loading,
    error,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions
  }
}
